import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ImageModalProps {
  images: { src: string; alt: string }[];
  initialIndex?: number;
  isOpen: boolean;
  onClose: () => void;
}

export default function ImageModal({ images, initialIndex = 0, isOpen, onClose }: ImageModalProps) {
  const [current, setCurrent] = useState(initialIndex);
  const [visible, setVisible] = useState(false);
  const [slideDir, setSlideDir] = useState<'left' | 'right' | null>(null);
  const [dragX, setDragX] = useState(0);
  const touchStart = useRef<number | null>(null);

  // 開くアニメーション
  useEffect(() => {
    if (isOpen) {
      setCurrent(initialIndex);
      setSlideDir(null);
      setDragX(0);
      // 次フレームでvisibleにしてCSS transitionを発火
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen, initialIndex]);

  // キーボード操作 & body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowRight') goWithDir('left');
      if (e.key === 'ArrowLeft') goWithDir('right');
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, images.length]);

  const goWithDir = useCallback((dir: 'left' | 'right') => {
    setSlideDir(dir);
    setCurrent(prev =>
      dir === 'left'
        ? (prev + 1) % images.length
        : (prev - 1 + images.length) % images.length
    );
    // アニメーション終了後にリセット
    setTimeout(() => setSlideDir(null), 300);
  }, [images.length]);

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => onClose(), 250);
  }, [onClose]);

  // タッチ: スワイプ追従
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
    setDragX(0);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (touchStart.current === null) return;
    setDragX(e.touches[0].clientX - touchStart.current);
  }, []);

  const handleTouchEnd = useCallback(() => {
    const threshold = 50;
    if (dragX < -threshold) {
      goWithDir('left');
    } else if (dragX > threshold) {
      goWithDir('right');
    }
    setDragX(0);
    touchStart.current = null;
  }, [dragX, goWithDir]);

  if (!isOpen) return null;

  const slideClass = slideDir
    ? `lp__modal__slide lp__modal__slide--${slideDir}`
    : 'lp__modal__slide';

  return createPortal(
    <div
      className={`lp__modal-overlay${visible ? ' lp__modal-overlay--visible' : ''}`}
      onClick={handleClose}
    >
      <div
        className={`lp__modal${visible ? ' lp__modal--visible' : ''}`}
        onClick={e => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <button className="lp__modal__close" onClick={handleClose} aria-label="閉じる">×</button>
        <div
          className={slideClass}
          style={dragX !== 0 ? { transform: `translateX(${dragX}px)`, transition: 'none' } : undefined}
        >
          <img src={images[current].src} alt={images[current].alt} />
        </div>
        {images.length > 1 && (
          <>
            <button className="lp__modal__arrow lp__modal__arrow--prev" onClick={() => goWithDir('right')} aria-label="前へ" />
            <button className="lp__modal__arrow lp__modal__arrow--next" onClick={() => goWithDir('left')} aria-label="次へ" />
            <div className="lp__modal__dots">
              {images.map((_, i) => (
                <span
                  key={i}
                  className={`lp__modal__dot${i === current ? ' lp__modal__dot--active' : ''}`}
                  onClick={() => {
                    const dir = i > current ? 'left' : 'right';
                    setSlideDir(dir);
                    setCurrent(i);
                    setTimeout(() => setSlideDir(null), 300);
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
